import { camelCase, upperFirst } from 'lodash-es';
import { getImg } from '@home/_config/getImgs';
import { LANGUAGE_EN } from '@home/_config/config';
import ConsoleGameIcon from '@/pages/home/assets/img/common/consolegame-default.png';
import type { OptionsItemType } from '@/pages/home/components/NewRegionSelect/type';
import type { TGroup, TOptionsItem } from '@sg/components/FilterSelect/types';
import { getRegionSelectedData } from '@/pages/dashboard/components/NewFilter/util';

export const ALL_SYSTEMS_PLATFORM = 'All Systems';

export interface ILiveTitleOsOption {
  os: string;
  os_name_en?: string;
  os_name_ch?: string;
  icon?: string;
}

export interface ILiveTitleOsGroup {
  type?: string | null;
  options?: ILiveTitleOsOption[];
}

interface IParseLiveTitleOsOptionsParams {
  locale?: string;
  searchValue?: string;
}

export const isAllSystemsOsOption = (option: ILiveTitleOsOption): boolean => (
  option.os === ALL_SYSTEMS_PLATFORM
  || option.os === '255'
  || option.os_name_en === ALL_SYSTEMS_PLATFORM
);

/** Downstream summary/timeline APIs expect `os_name_en`, not raw `os` codes. */
export const getPlatformPayloadValue = (option: ILiveTitleOsOption): string => (
  option.os_name_en || option.os
);

export const hasExplicitPlatformTypes = (
  liveTitleOs: ILiveTitleOsGroup[] | undefined,
): boolean => (
  Array.isArray(liveTitleOs) && liveTitleOs.some((group) => Boolean(group?.type))
);

const matchesSearch = (
  option: ILiveTitleOsOption,
  groupType: string | null | undefined,
  searchValue: string,
): boolean => {
  const keyword = searchValue.trim().toLowerCase();
  if (!keyword) {
    return true;
  }
  const candidates = [
    option.os,
    option.os_name_en,
    option.os_name_ch,
    groupType,
  ];
  return candidates.some(
    (candidate) => candidate && String(candidate).toLowerCase().includes(keyword),
  );
};

const resolveGroupKey = (
  groupType: string | null | undefined,
  option: ILiveTitleOsOption,
): string => groupType || option.os_name_en || option.os;

// getImgs maps by platform full name; try os + localized names before API icon fallback.
const normalizePlatformIconKey = (value: string): string => (
  value.toLowerCase().replace(/[^a-z0-9]/g, '')
);

const resolvePlatformIcon = (option: ILiveTitleOsOption): string => {
  const candidates = [option.os, option.os_name_en, option.os_name_ch]
    .filter(Boolean) as string[];

  for (const candidate of candidates) {
    const mapped = getImg(upperFirst(camelCase(candidate)))
      || getImg(candidate)
      || getImg(normalizePlatformIconKey(candidate));
    if (mapped) {
      return mapped;
    }
  }

  return option.icon || ConsoleGameIcon;
};

const buildFilterSelectOption = (
  option: ILiveTitleOsOption,
  groupKey: string,
  locale?: string,
): TOptionsItem => {
  const isEN = locale === LANGUAGE_EN;
  return {
    value: getPlatformPayloadValue(option),
    text: (isEN ? option.os_name_en : option.os_name_ch)
      || option.os_name_en
      || option.os,
    type: groupKey,
    icon: resolvePlatformIcon(option),
  };
};

const mapPlatformChild = (
  option: ILiveTitleOsOption,
  isEN: boolean,
): OptionsItemType => {
  return {
    value: getPlatformPayloadValue(option),
    text: (isEN ? option.os_name_en : option.os_name_ch)
      || option.os_name_en
      || option.os,
    textCN: option.os_name_ch || option.os_name_en || option.os,
    icon: resolvePlatformIcon(option),
  };
};

/** Original flat/grouped FilterSelect options — used when all `type` values are null. */
export const parseLiveTitleOsOptions = (
  liveTitleOs: ILiveTitleOsGroup[] | undefined,
  { locale, searchValue = '' }: IParseLiveTitleOsOptionsParams = {},
): { options: TOptionsItem[]; group?: TGroup } => {
  if (!Array.isArray(liveTitleOs) || !liveTitleOs.length) {
    return { options: [] };
  }

  const flattened = liveTitleOs.flatMap((groupItem) => (
    (groupItem.options || [])
      .filter((option) => option?.os && !isAllSystemsOsOption(option))
      .filter((option) => matchesSearch(option, groupItem.type, searchValue))
      .map((option) => ({
        option,
        groupKey: resolveGroupKey(groupItem.type, option),
        hasExplicitType: Boolean(groupItem.type),
      }))
  ));

  if (!flattened.length) {
    return { options: [] };
  }

  const options = flattened.map(({ option, groupKey }) => (
    buildFilterSelectOption(option, groupKey, locale)
  ));

  const hasExplicitGroup = flattened.some((item) => item.hasExplicitType);
  if (!hasExplicitGroup) {
    return { options };
  }

  const group = flattened.reduce<TGroup>((acc, { groupKey }) => {
    acc[groupKey] = groupKey;
    return acc;
  }, {});

  return { options, group };
};

export const buildPlatformRegionSelectOptions = (
  liveTitleOs: ILiveTitleOsGroup[] | undefined,
  locale?: string,
): { options: OptionsItemType[]; isGrouped: boolean } => {
  if (!Array.isArray(liveTitleOs) || !liveTitleOs.length) {
    return { options: [], isGrouped: false };
  }

  const isEN = locale === LANGUAGE_EN;
  const hasExplicitType = hasExplicitPlatformTypes(liveTitleOs);

  if (!hasExplicitType) {
    return { options: [], isGrouped: false };
  }

  const options = liveTitleOs.flatMap((group) => {
    const children = (group.options || [])
      .filter((option) => option?.os && !isAllSystemsOsOption(option))
      .map((option) => mapPlatformChild(option, isEN));

    if (!children.length) {
      return [];
    }

    if (group.type) {
      return [{
        value: group.type,
        text: group.type,
        textCN: group.type,
        children,
      }];
    }

    // Mixed types: null-type rows fall back to os_name_en as the group key.
    return children.map((child) => ({
      value: child.value,
      text: child.text,
      textCN: child.textCN,
      children: [child],
    }));
  });

  return {
    options,
    isGrouped: true,
  };
};

export const toRegionSelectValue = (
  selectedPlatforms: string[] | undefined,
): { regions: string[]; markets: string[]; type: string } => {
  // Keep parent `selectedPlatforms` reference in `markets` (same as LiveRegionFilter passing selectedRegion through).
  const markets = selectedPlatforms?.length
    ? selectedPlatforms
    : [ALL_SYSTEMS_PLATFORM];

  return {
    regions: [],
    markets,
    type: 'market',
  };
};

export const resolveRegionSelectValue = (
  selectedPlatforms: string[] | undefined,
  options: OptionsItemType[],
) => getRegionSelectedData(options, toRegionSelectValue(selectedPlatforms));

export const fromRegionSelectValue = (
  value: { regions?: string[]; markets?: string[]; type?: string } | null,
): string[] => {
  const markets = value?.markets;
  if (!markets?.length) {
    return [ALL_SYSTEMS_PLATFORM];
  }
  return markets;
};
